import { useCallback, useEffect, useRef, useState } from "react";

import { getAttendanceDeviceData } from "components/attendance/helpers";
import useAttendanceMutationScan from "./attendance-use-mutation-scan";


export const ATTENDANCE_SCAN_STATES = {
    IDLE: "idle",
    SCANNING: "scanning",
    LOCATING: "locating",
    SUBMITTING: "submitting",
    RESULT: "result",
    FAILED: "failed"
};

export const ATTENDANCE_ACTIONS = {
    CHECK_IN: "check_in",
    CHECK_OUT: "check_out"
};

const GEOLOCATION_OPTIONS = {
    enableHighAccuracy: true,
    timeout: 10000,
    maximumAge: 0
};

const QR_SCANNER_CONFIG = {
    fps: 10,
    qrbox: {
        width: 240,
        height: 240
    }
};

const FALLBACK_CAMERA_CONFIG = { facingMode: "environment" };

const REAR_CAMERA_LABEL_PATTERN = /back|rear|environment|world|zad|tyl|spate|arriere|rueck|ruck/i;

const getReaderId = () => `attendance-reader-${ Date.now() }-${ Math.random().toString(16).slice(2) }`;

const waitForReaderElement = (readerId, timeout = 2000) => new Promise((resolve) => {
    const startedAt = Date.now();

    const check = () => {
        const readerElement = document.getElementById(readerId);

        if (readerElement) {
            resolve(readerElement);

            return;
        }

        if (Date.now() - startedAt >= timeout) {
            resolve(null);

            return;
        }

        window.requestAnimationFrame(check);
    };

    check();
});

const getSecureContextFailure = (kind = "camera") => ({
    kind,
    reason: "insecure_context",
    title: "ATTENDANCE.Error_SecureContext_Title",
    text: "ATTENDANCE.Error_SecureContext_Text"
});

const isSecureBrowserContext = () => (
    typeof window === "undefined" || window.isSecureContext !== false
);

const loadHtml5Qrcode = async () => {
    const module = await import("html5-qrcode");

    return module.Html5Qrcode;
};

const RESULT_BY_STATUS = {
    success: "accepted",
    warning: "ignored",
    rejected: "rejected"
};

const normalizeReason = (reason) => String(reason || "")
    .trim()
    .toLowerCase()
    .replace(/[\s-]+/g, "_");

const normalizeResult = (payload) => {
    const workSessionLog = payload?.workSessionLog || {};
    const status = String(workSessionLog.status || "").toLowerCase();

    return {
        result: RESULT_BY_STATUS[status] || "rejected",
        status,
        reason: normalizeReason(workSessionLog.reason),
        detail: workSessionLog.detail || payload?.message || "",
        label: payload?.label || "",
        message: payload?.message || "",
        server_time: workSessionLog.created_at,
        attendance_shift_id: workSessionLog.work_session_id,
        action: workSessionLog.action
    };
};

const getErrorMessage = (error) => {
    if (!error) {
        return "";
    }

    if (typeof error === "string") {
        return error;
    }

    return error.message || error.name || String(error);
};

const isCameraPermissionError = (error) => (
    /notallowed|permission|denied|securityerror/i.test(getErrorMessage(error))
);

const isCameraNotFoundError = (error) => (
    /notfound|overconstrained|constraint|no camera|requested device not found/i.test(getErrorMessage(error))
);

const getCameraFailure = (error) => {
    if (!window.navigator?.mediaDevices?.getUserMedia) {
        return {
            kind: "camera",
            reason: "camera_unsupported",
            title: "ATTENDANCE.Error_Camera_Title",
            text: "ATTENDANCE.Error_Camera_Text"
        };
    }

    if (isCameraPermissionError(error)) {
        return {
            kind: "camera",
            reason: "camera_permission_denied",
            title: "ATTENDANCE.Error_CameraPermission_Title",
            text: "ATTENDANCE.Error_CameraPermission_Text"
        };
    }

    if (isCameraNotFoundError(error)) {
        return {
            kind: "camera",
            reason: "camera_not_found",
            title: "ATTENDANCE.Error_CameraNotFound_Title",
            text: "ATTENDANCE.Error_CameraNotFound_Text"
        };
    }

    return {
        kind: "camera",
        reason: "camera_unavailable",
        title: "ATTENDANCE.Error_Camera_Title",
        text: "ATTENDANCE.Error_Camera_Text"
    };
};

const getCameraStartConfigs = async (Html5Qrcode) => {
    let cameras = [];

    try {
        cameras = await Html5Qrcode.getCameras();
    } catch (e) {
        if (isCameraPermissionError(e) || isCameraNotFoundError(e)) {
            throw e;
        }

        // Enumeration can be unreliable on mobile Safari; still try facingMode.
        return [ FALLBACK_CAMERA_CONFIG ];
    }

    if (!Array.isArray(cameras) || cameras.length === 0) {
        return [ FALLBACK_CAMERA_CONFIG ];
    }

    const rearCamera = cameras.find((camera) => (
        REAR_CAMERA_LABEL_PATTERN.test(camera?.label || "")
    ));
    const firstCamera = cameras.find((camera) => camera?.id && camera.id !== rearCamera?.id);

    return [
        rearCamera?.id,
        FALLBACK_CAMERA_CONFIG,
        firstCamera?.id
    ].filter(Boolean);
};

const startScannerWithConfigs = async (scanner, configs, onScanSuccess) => {
    let lastError = null;

    for (const cameraConfig of configs) {
        try {
            await scanner.start(
                cameraConfig,
                QR_SCANNER_CONFIG,
                onScanSuccess
            );

            return;
        } catch (e) {
            lastError = e;

            if (isCameraPermissionError(e)) {
                throw e;
            }
        }
    }

    throw lastError || new Error("Camera unavailable");
};

const disposeScanner = async (scanner) => {
    if (!scanner) {
        return;
    }

    try {
        await scanner.stop();
    } catch (e) {
        // A scanner can be disposed before start has completed.
    }

    try {
        await scanner.clear();
    } catch (e) {
        // Clearing is best-effort after the media tracks are stopped.
    }
};

const getGeolocationFailure = (error) => {
    if (error?.code === 1) {
        return {
            kind: "location",
            reason: "permission_denied",
            title: "ATTENDANCE.Error_GpsPermission_Title",
            text: "ATTENDANCE.Error_GpsPermission_Text"
        };
    }

    if (error?.code === 3) {
        return {
            kind: "location",
            reason: "gps_timeout",
            title: "ATTENDANCE.Error_GpsTimeout_Title",
            text: "ATTENDANCE.Error_GpsTimeout_Text"
        };
    }

    return {
        kind: "location",
        reason: "gps_unavailable",
        title: "ATTENDANCE.Error_GpsUnavailable_Title",
        text: "ATTENDANCE.Error_GpsUnavailable_Text"
    };
};

const useAttendanceScan = ({ action, eeId }) => {
    const scannerRef = useRef(null);
    const scannerStartRef = useRef(null);
    const scannerCleanupRef = useRef(Promise.resolve());
    const scanLockedRef = useRef(false);
    const qrPayloadRef = useRef(null);
    const operationIdRef = useRef(0);
    const mountedRef = useRef(false);

    const [ readerId, setReaderId ] = useState(getReaderId);
    const [ scanState, setScanState ] = useState(ATTENDANCE_SCAN_STATES.IDLE);
    const [ result, setResult ] = useState(null);
    const [ failure, setFailure ] = useState(null);

    const { attendanceScan } = useAttendanceMutationScan();

    const setStateIfMounted = useCallback((callback) => {
        if (mountedRef.current) {
            callback();
        }
    }, []);

    const isActiveOperation = useCallback((operationId) => (
        mountedRef.current && operationIdRef.current === operationId
    ), []);

    const queueScannerDisposal = useCallback((scanner, startPromise = null) => {
        const cleanupPromise = scannerCleanupRef.current
            .catch(() => undefined)
            .then(async () => {
                if (startPromise) {
                    try {
                        await startPromise;
                    } catch (e) {
                        // A failed start still needs its reader element cleared.
                    }
                }

                await disposeScanner(scanner);
            });

        scannerCleanupRef.current = cleanupPromise;

        return cleanupPromise;
    }, []);

    const stopScanner = useCallback(() => {
        const startRecord = scannerStartRef.current;
        const scanner = scannerRef.current || startRecord?.scanner;

        if (!scanner) {
            return scannerCleanupRef.current;
        }

        scannerRef.current = null;

        if (startRecord?.scanner === scanner) {
            scannerStartRef.current = null;
        }

        return queueScannerDisposal(scanner, startRecord?.promise);
    }, [ queueScannerDisposal ]);

    const submitScan = useCallback(async (qrPayload, position, operationId) => {
        if (!isActiveOperation(operationId)) {
            return;
        }

        const { latitude, longitude, accuracy } = position.coords || {};

        setStateIfMounted(() => {
            setScanState(ATTENDANCE_SCAN_STATES.SUBMITTING);
        });

        try {
            const { data } = await attendanceScan({
                ee_id: eeId,
                action,
                qr_payload: qrPayload,
                latitude,
                longitude,
                accuracy,
                ...getAttendanceDeviceData()
            });

            const scanPayload = data?.workSessionLogScan;

            if (!scanPayload) {
                throw new Error("Empty work session scan payload");
            }

            if (!isActiveOperation(operationId)) {
                return;
            }

            setStateIfMounted(() => {
                setResult(normalizeResult(scanPayload));
                setScanState(ATTENDANCE_SCAN_STATES.RESULT);
            });
        } catch (e) {
            if (!isActiveOperation(operationId)) {
                return;
            }

            setStateIfMounted(() => {
                setFailure({
                    kind: "server",
                    reason: "server_error",
                    title: "ATTENDANCE.Error_Server_Title",
                    text: "ATTENDANCE.Error_Server_Text",
                    detail: e?.message || ""
                });
                setScanState(ATTENDANCE_SCAN_STATES.FAILED);
            });
        }
    }, [
        action,
        attendanceScan,
        eeId,
        isActiveOperation,
        setStateIfMounted
    ]);

    const requestLocation = useCallback((qrPayload, operationId = operationIdRef.current) => {
        if (!qrPayload || !isActiveOperation(operationId)) {
            return;
        }

        qrPayloadRef.current = qrPayload;

        setStateIfMounted(() => {
            setFailure(null);
            setScanState(ATTENDANCE_SCAN_STATES.LOCATING);
        });

        if (!isSecureBrowserContext()) {
            setStateIfMounted(() => {
                setFailure(getSecureContextFailure("location"));
                setScanState(ATTENDANCE_SCAN_STATES.FAILED);
            });

            return;
        }

        if (!window.navigator?.geolocation) {
            setStateIfMounted(() => {
                setFailure({
                    kind: "location",
                    reason: "gps_unsupported",
                    title: "ATTENDANCE.Error_GpsUnavailable_Title",
                    text: "ATTENDANCE.Error_GpsUnavailable_Text"
                });
                setScanState(ATTENDANCE_SCAN_STATES.FAILED);
            });

            return;
        }

        window.navigator.geolocation.getCurrentPosition(
            (position) => {
                if (isActiveOperation(operationId)) {
                    submitScan(qrPayload, position, operationId);
                }
            },
            (error) => {
                if (!isActiveOperation(operationId)) {
                    return;
                }

                setStateIfMounted(() => {
                    setFailure(getGeolocationFailure(error));
                    setScanState(ATTENDANCE_SCAN_STATES.FAILED);
                });
            },
            GEOLOCATION_OPTIONS
        );
    }, [
        isActiveOperation,
        setStateIfMounted,
        submitScan
    ]);

    const handleScanSuccess = useCallback((decodedText) => {
        if (scanLockedRef.current) {
            return;
        }

        scanLockedRef.current = true;
        qrPayloadRef.current = decodedText;
        const operationId = operationIdRef.current;

        stopScanner().finally(() => {
            requestLocation(decodedText, operationId);
        });
    }, [
        requestLocation,
        stopScanner
    ]);

    const startScanner = useCallback(async () => {
        const operationId = operationIdRef.current + 1;

        operationIdRef.current = operationId;
        scanLockedRef.current = false;
        qrPayloadRef.current = null;

        setResult(null);
        setFailure(null);

        await stopScanner();

        if (!isActiveOperation(operationId)) {
            return;
        }

        if (!isSecureBrowserContext()) {
            setFailure(getSecureContextFailure());
            setScanState(ATTENDANCE_SCAN_STATES.FAILED);

            return;
        }

        if (!window.navigator?.mediaDevices?.getUserMedia) {
            setFailure(getCameraFailure());
            setScanState(ATTENDANCE_SCAN_STATES.FAILED);

            return;
        }

        const nextReaderId = getReaderId();

        setReaderId(nextReaderId);

        const readerElement = await waitForReaderElement(nextReaderId);

        if (!isActiveOperation(operationId)) {
            return;
        }

        if (!readerElement) {
            setFailure(getCameraFailure());
            setScanState(ATTENDANCE_SCAN_STATES.FAILED);

            return;
        }

        const Html5Qrcode = await loadHtml5Qrcode();

        if (
            !isActiveOperation(operationId) ||
            !document.getElementById(nextReaderId)
        ) {
            return;
        }

        const scanner = new Html5Qrcode(nextReaderId, false);

        scannerRef.current = scanner;
        setScanState(ATTENDANCE_SCAN_STATES.SCANNING);

        const startPromise = (async () => {
            const cameraConfigs = await getCameraStartConfigs(Html5Qrcode);

            if (
                !isActiveOperation(operationId) ||
                scannerRef.current !== scanner
            ) {
                return false;
            }

            await startScannerWithConfigs(scanner, cameraConfigs, handleScanSuccess);

            return true;
        })();

        scannerStartRef.current = {
            scanner,
            promise: startPromise
        };

        try {
            const scannerStarted = await startPromise;

            if (scannerStartRef.current?.scanner === scanner) {
                scannerStartRef.current = null;
            }

            if (!scannerStarted) {
                if (scannerRef.current === scanner) {
                    scannerRef.current = null;
                    await queueScannerDisposal(scanner);
                } else {
                    await scannerCleanupRef.current;
                }

                return;
            }

            if (
                !isActiveOperation(operationId) ||
                scannerRef.current !== scanner
            ) {
                if (scannerRef.current === scanner) {
                    scannerRef.current = null;
                }

                await queueScannerDisposal(scanner);

                return;
            }

        } catch (e) {
            if (scannerStartRef.current?.scanner === scanner) {
                scannerStartRef.current = null;
            }

            if (scannerRef.current === scanner) {
                scannerRef.current = null;
            }

            await queueScannerDisposal(scanner);

            if (isActiveOperation(operationId)) {
                setFailure(getCameraFailure(e));
                setScanState(ATTENDANCE_SCAN_STATES.FAILED);
            }
        }
    }, [
        handleScanSuccess,
        isActiveOperation,
        queueScannerDisposal,
        stopScanner
    ]);

    const retry = useCallback(() => {
        if (failure?.kind === "location" && qrPayloadRef.current) {
            operationIdRef.current += 1;
            requestLocation(qrPayloadRef.current, operationIdRef.current);

            return;
        }

        startScanner();
    }, [
        failure,
        requestLocation,
        startScanner
    ]);

    const reset = useCallback(() => {
        operationIdRef.current += 1;
        scanLockedRef.current = false;
        qrPayloadRef.current = null;
        setResult(null);
        setFailure(null);
        setScanState(ATTENDANCE_SCAN_STATES.IDLE);
    }, []);

    useEffect(() => {
        mountedRef.current = true;

        return () => {
            mountedRef.current = false;
            operationIdRef.current += 1;
            stopScanner();
        };
    }, [ stopScanner ]);

    return {
        readerId,
        scanState,
        result,
        failure,
        startScanner,
        stopScanner,
        retry,
        reset
    };
};

export default useAttendanceScan;
